import React, { useCallback, useContext, useEffect, useState } from "react"
import cn from "classnames"
import { useTranslation } from "react-i18next"
import LoadingButton from "../LoadingButton/LoadingButton"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    faAngleUp,
    faBell,
    faEllipsisHAlt,
    faExchangeAlt,
    faExclamationCircle,
    faExpandArrows,
    faFile,
    faFileEdit,
    faMinusCircle,
    faNewspaper,
    faShareAlt,
    faSync,
    faTrashAlt,
    faHistory,
    faFileImport
} from "@fortawesome/pro-light-svg-icons"
import { Button } from "react-bootstrap"
import { Article, ArticleAction, ArticleStatusType } from "../../models/article"
import { usePopoverShown } from "../../utility/knowledgeBase/usePopoverShown"
import ArticleSharePopover from "../ArticleSharePopover/ArticleSharePopover"
import styles from "./ArticleActions.module.scss"
import { ModifyKnowledgeBaseSubscriptions, PublishIntent } from "../../permissions"
import { getSubscriptionOptions } from "../../utility/knowledgeBase/knowledgeBase"
import { buildArticleActionUrl } from "../../utility/knowledgeBase/articleUrlHelper"
import Can from "../Can/Can"
import IconButton from "../IconButton/IconButton"
import ArticleSubscriptionPopover from "../ArticleSubscriptionPopover/ArticleSubscriptionPopover"
import SettingsContextMenu from "../SettingsContextMenu/SettingsContextMenu"
import SettingsContextMenuItem from "../SettingsContextMenuItem/SettingsContextMenuItem"
import {
    ArticleSubscriptionFormValues,
    KnowledgeBaseArticleSubscriptions
} from "../../models/knowledgeBase/subscription"
import Async from "../Async/Async"
import ArticleActionItemLoader from "./ArticleActionItemLoader"
import ArticleViewContext from "../ArticleViewProvider/ArticleViewContext"
import { shallowEqual } from "react-redux"
import {
    selectArticleCompareMode,
    selectArticleExpanded,
    selectCurrentAnswerId,
    selectCurrentArticle,
    selectCurrentBranch,
    selectCurrentCatalog,
    selectIsArticleHistoryMod,
    selectPreviousVersionArticleId,
    selectRelatedCategoriesState,
    selectRemoveArticleSharingState,
    selectShareArticleState,
    selectUpdateArticleState
} from "../../store/knowledgeBase/selectors"
import usePermissionsCheck from "../../hooks/usePermissionsCheck"
import { push } from "connected-react-router"
import {
    createDraftFromArticle,
    getArticleCompare,
    getArticleSharing,
    publishArticle,
    removeArticle,
    removeArticleSharing,
    setUnpublishedStatusForArticle,
    shareArticle
} from "../../store/knowledgeBase/thunks"
import { selectAllProjects, selectCurrentProject } from "../../store/projects/selectors"
import { updateSubscriptions } from "../../store/knowledgeBaseSubscriptions/thunks"
import {
    selectCurrentSubscriptionsState,
    selectUpdateSubscriptionsState
} from "../../store/knowledgeBaseSubscriptions/selectors"
import KnowledgeBaseContext from "../KnowledgeBaseProvider/KnowledgeBaseContext"
import { offCompareMode, revertArticleChanges } from "../../store/knowledgeBase/actions"
import KnowledgeBasePermissionsProvider from "../KnowledgeBasePermissionsProvider/KnowledgeBasePermissionsProvider"
import { ScenarioContext } from "../ScenarioEditor/ScenarioContext"
import { selectCurrentUserLogin } from "../../store/users/selectors"
import useRefCheck from "../../hooks/useRefCheck"
import { faArrowToBottom } from "@fortawesome/pro-light-svg-icons/faArrowToBottom"
import useDownloadKBCatalogFile from "../../utility/knowledgeBase/useDownloadKBCatalogFile"
import { DocType } from "../../models/knowledgeBase/converter"
import { actions } from "../../store/knowledgeBase/slice"
import { SHARING_ENABLED } from "../../utility/common/flags"
import { useModal } from "../../hooks/useModal"
import AlertDialog from "../AlertDialog/AlertDialog"
import { formTranslation } from "../../locales/form"
import { approveNewsAfterArticleDelete } from "../../utility/common/approveNewsAfterArticleDelete"
import TooltipTrigger from "../TooltipTrigger/TooltipTrigger"
import FavoriteArticleButton from "../FavoriteArticleButton/FavoriteArticleButton"
import { NewslineSettings } from "../../models/knowledgeBase/newsline"
import CommonModal from "../CommonModal/CommonModal"
import ArticleSearchModalForm, { ArticleSearchModalFormType } from "../CommonModal/modalForms/ArticleSearchModalForm"
import { FormikProps } from "formik"
import { ArticleCompareMode } from "../../models/articleCompare"
import AddButton from "../AddButton/AddButton"
import { useAppDispatch, useAppSelector } from "../../store/hooks"
import ArticleImportFilesModalForm, {
    ArticleImportFilesModalFormType
} from "../CommonModal/modalForms/ArticleImportFilesModalForm/ArticleImportFilesModalForm"
import DroppableArea from "../DroppableArea/DroppableArea"
import useImportFiles from "../../utility/knowledgeBase/useImportFiles"
import { testId } from "../../utility/tests/testId/knowledgeBase"
import ArticlePublishPopover from "../ArticlePublishPopover/ArticlePublishPopover"
import { ArticlePublishInfo, KnowledgeBaseArticlePublishType } from "../../models/knowledgeBase/knowledgeBasePublish"

const tNamespace = "knowledgeBase:"
const tActionsNamespace = `${tNamespace}article-actions.`
const tScenarioEditorNamespace = "scenarioEditor:"
const tFormNamespace = "form:"

export interface ArticleActionsProps {
    code: string
    article: Article
    isEdit: boolean
    disableAddBlockBtn?: boolean
    disableEditButtons: boolean
    onSave: () => void
    onOpenApproveModal: () => void
    onRefresh?: () => void
    onOpenInfo: (article: Article, isEdit?: boolean) => void
    onOpenSurvey: (article: Article, isEdit?: boolean) => void
    onOpenNewsline: (newsline?: NewslineSettings) => void
    onOpenHistory?: (article: Article) => void
    onExpandArticle?: (e: React.SyntheticEvent) => void
    onUnpublishArticle?: () => void
    isScenario?: boolean
}

const ArticleActions: React.FC<ArticleActionsProps> = props => {
    const {
        code,
        article,
        isEdit,
        onSave,
        onOpenApproveModal,
        onRefresh,
        onOpenInfo,
        onOpenSurvey,
        onOpenNewsline,
        onOpenHistory,
        onExpandArticle,
        disableAddBlockBtn,
        disableEditButtons,
        isScenario = false,
        onUnpublishArticle
    } = props

    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const { connected, onShare } = useContext(ArticleViewContext)
    const { url, permittedActionOptions, projectId } = useContext(KnowledgeBaseContext)
    const { handleAddBlock } = useContext(ScenarioContext)

    const currentProject = useAppSelector(selectCurrentProject)
    const projects = useAppSelector(selectAllProjects)
    const updateArticleState = useAppSelector(selectUpdateArticleState)
    const subscriptionsState = useAppSelector(selectCurrentSubscriptionsState)
    const updateSubscriptionsState = useAppSelector(selectUpdateSubscriptionsState)
    const currentAnswerId = useAppSelector(selectCurrentAnswerId)
    const sharingArticleState = useAppSelector(selectShareArticleState)
    const removeArticleSharingState = useAppSelector(selectRemoveArticleSharingState)
    const login = useAppSelector(selectCurrentUserLogin)
    const publishAllowed = usePermissionsCheck([PublishIntent])
    const isSharingEnabled = useRefCheck(SHARING_ENABLED)
    const { CurrentCatalogCode, CurrentCategoryId } = useAppSelector(selectCurrentBranch, shallowEqual)
    const isArticleExpanded = useAppSelector(selectArticleExpanded)
    const relatedArticlesState = useAppSelector(selectRelatedCategoriesState, shallowEqual)
    const articleCompareMode = useAppSelector(selectArticleCompareMode)
    const currentArticle = useAppSelector(selectCurrentArticle)
    const isArticleHistoryMode = useAppSelector(selectIsArticleHistoryMod)
    const currentCatalog = useAppSelector(selectCurrentCatalog)
    const previousVersionArticleId = useAppSelector(selectPreviousVersionArticleId)
    const { importFiles } = useImportFiles(CurrentCatalogCode ?? "", CurrentCategoryId ?? "")

    const isArticleLocked = currentArticle?.IsLocked
    const isCompareMode = articleCompareMode !== ArticleCompareMode.Default
    const isFavoriteArticleButtonVisible = !(
        isArticleHistoryMode ||
        article.Status === ArticleStatusType.Removed ||
        isEdit
    )
    const isDelayedPublish =
        article.PublishInfo &&
        article.PublishInfo.Type === KnowledgeBaseArticlePublishType.Timeout &&
        (article.PublishInfo.PublishDate || article.PublishInfo.UnpublishDate) &&
        !article.PublishInfo.IsCompleted

    const showCreateDraftFromButton = previousVersionArticleId !== undefined || (isArticleHistoryMode && !isCompareMode)

    const { modalOpen, openModal, closeModal, onExited } = useModal(() => (
        <CommonModal<ArticleSearchModalFormType>
            show={modalOpen}
            onClose={closeModal}
            onSubmit={(values: ArticleSearchModalFormType) => {
                if (projectId && currentArticle?.Article?.Id && values.Id)
                    dispatch(getArticleCompare(projectId, currentArticle.Article.Id, values.Id))
                closeModal()
            }}
            headerText={t(`${tNamespace}article-actions.compare-articles-header`)}
            onExited={onExited}
            renderBody={(formikProps: FormikProps<ArticleSearchModalFormType>) => (
                <ArticleSearchModalForm
                    projectId={projectId ? projectId : ""}
                    filter={
                        currentArticle && currentArticle.Article?.SymbolCode
                            ? [currentArticle.Article?.SymbolCode]
                            : undefined
                    }
                    {...formikProps}
                />
            )}
            initialValues={{}}
            closeBtnText={t(`${tNamespace}article-actions.close-button`)}
            submitBtnText={t(`${tNamespace}article-actions.compare-button`)}
        />
    ))

    const [isPendingShare, setPendingShare] = useState(false)

    const {
        modalOpen: removeArticleModalOpen,
        openModal: removeArticleOpenModal,
        closeModal: removeArticleCloseModal,
        onExited: removeArticleOnExited
    } = useModal(() => (
        <AlertDialog
            show={removeArticleModalOpen}
            title={t(`${tActionsNamespace}remove-article-delete-confirmation.title`)}
            message={t(`${tActionsNamespace}remove-article-delete-confirmation.message`)}
            submitBtnText={t(formTranslation.delete)}
            onClose={removeArticleCloseModal}
            onSubmit={handleRemoveArticle}
            variant="danger"
            onExited={removeArticleOnExited}
        />
    ))

    const {
        modalOpen: isImportFilesModalOpen,
        openModal: importFilesOpenModal,
        closeModal: importFilesCloseModal,
        onExited: importFilesOnExited
    } = useModal(() => (
        <CommonModal<ArticleImportFilesModalFormType>
            show={isImportFilesModalOpen}
            onClose={importFilesCloseModal}
            onSubmit={async values => {
                importFilesCloseModal()

                if (!values.Files.length) {
                    return
                }

                await importFiles(values.Files)
            }}
            headerText={t(`${tNamespace}article-actions.import-files-header`)}
            onExited={importFilesOnExited}
            renderBody={() => (
                <DroppableArea isHidePreview acceptFilesFormat={[".doc", ".docx"]} isUploadFile={false}>
                    <ArticleImportFilesModalForm />
                </DroppableArea>
            )}
            initialValues={{ Files: [] }}
            closeBtnText={t(`${tNamespace}article-actions.close-button`)}
            submitBtnText={t(`${tNamespace}article-actions.import-button`)}
            isCenterModal
        />
    ))

    useEffect(() => {
        projectId && isSharingEnabled && dispatch(getArticleSharing(projectId, code))
    }, [code, dispatch, isSharingEnabled, projectId])

    const handlePublishArticle = useCallback(
        (publishInfo: ArticlePublishInfo) => {
            const id = article.Id
            const hasData = projectId && code && id

            if (hasData) {
                dispatch(publishArticle(projectId, url, code, id, publishInfo))
                dispatch(actions.updateArticleInFavoriteBlock({ id: article.SymbolCode, title: article.Title }))
            }
        },
        [article, projectId, code, dispatch, url]
    )

    const handleEdit = useCallback(() => {
        dispatch(push(buildArticleActionUrl(url, ArticleAction.Edit, code)))
    }, [dispatch, url, code])

    const handleCancelEdit = useCallback(() => {
        dispatch(push(buildArticleActionUrl(url, ArticleAction.View, code)))
        revertArticleChanges(article, dispatch, currentAnswerId)
    }, [dispatch, url, code, article, currentAnswerId])

    const handleSetUnpublishedStatus = useCallback(() => {
        const id = article.Id
        projectId && code && id && dispatch(setUnpublishedStatusForArticle(projectId, url, code, id))
    }, [article.Id, code, dispatch, projectId, url])

    const handleRemoveArticle = useCallback(() => {
        const id = article.Id
        const symbolCode = article.SymbolCode

        if (projectId && code && id) {
            dispatch(removeArticle(projectId, url, code, id))

            // необходимло для удаления уведомлений у пользователей после удаления статьи
            // без этого пользователи никак не могут убрать уведомления, если они с подтверждением
            // а при переходе по уведомлению показывается ошибка, что статьи нет
            approveNewsAfterArticleDelete(projectId, article.SymbolCode)
                .then(() =>
                    console.info(`Article (with symble code ${article.SymbolCode}) notifications was deleted success`)
                )
                .catch(error => console.error(`Error when deleting article notifications: ${error}`))

            if (CurrentCatalogCode && CurrentCategoryId) {
                dispatch(
                    actions.removeArticleCategories({ CatalogCode: CurrentCatalogCode, CategoryId: CurrentCategoryId })
                )
            }
            dispatch(actions.removeArticleInFavoriteBlock(symbolCode))
        }
    }, [article.Id, projectId, code, dispatch, url, CurrentCatalogCode, CurrentCategoryId])

    const handleSubmitSubscriptions = useCallback(
        (values: ArticleSubscriptionFormValues, hidePopover: () => void) => {
            projectId && dispatch(updateSubscriptions(projectId, code, values, hidePopover))
        },
        [projectId, dispatch, code]
    )

    const handleCreateArticleSharing = useCallback(() => {
        projectId && login && dispatch(shareArticle(projectId, code, article.Id, login))
        setPendingShare(true)
    }, [article.Id, code, dispatch, login, projectId])

    const handleDeleteArticleSharing = useCallback(() => {
        projectId && login && dispatch(removeArticleSharing(projectId, code, article.Id, login))
        setPendingShare(false)
    }, [article.Id, code, dispatch, login, projectId])

    const { handleDownloadCatalog, downloadLink } = useDownloadKBCatalogFile(
        DocType.Word,
        projectId,
        CurrentCatalogCode,
        code
    )

    const {
        shown: articleSharePopoverShown,
        showPopover: showArticleSharePopover,
        hidePopover: hideArticleSharePopover
    } = usePopoverShown()

    const {
        shown: articleSubscribePopoverShown,
        showPopover: showArticleSubscribePopover,
        hidePopover: hideArticleSubscribePopover
    } = usePopoverShown()

    const {
        shown: articlePublishPopoverShown,
        showPopover: showArticlePublishPopover,
        hidePopover: hideArticlePublishPopover
    } = usePopoverShown()

    const publishButtonShown =
        article.Status === ArticleStatusType.Draft ||
        ((article.Status === ArticleStatusType.Inactive || article.Status === ArticleStatusType.Approval) &&
            publishAllowed)

    const handleCancelArticlePopover = useCallback(() => {
        isPendingShare && handleDeleteArticleSharing()
        hideArticleSharePopover()
    }, [isPendingShare, handleDeleteArticleSharing, hideArticleSharePopover])

    const handlePublish = useCallback(
        (publishInfo: ArticlePublishInfo) => {
            handlePublishArticle(publishInfo)
        },
        [handlePublishArticle, onOpenApproveModal, publishAllowed]
    )

    const handleClickPublishButton = useCallback(() => {
        if (publishAllowed) {
            articlePublishPopoverShown ? hideArticlePublishPopover() : showArticlePublishPopover()
        } else {
            onOpenApproveModal()
        }
    }, [publishAllowed, onOpenApproveModal, hideArticlePublishPopover, showArticlePublishPopover])

    const [editButtonTitle, editButtonCallback] = isEdit
        ? [t(`${tFormNamespace}cancel`), handleCancelEdit]
        : [t(`${tFormNamespace}change`), handleEdit]

    const subscriptionOptions = getSubscriptionOptions(t)

    const handleAddBlockClick = () => handleAddBlock()

    const getAdditionalSettings = useCallback(() => {
        const settings = []

        if (onExpandArticle && !isArticleHistoryMode) {
            settings.push(
                <SettingsContextMenuItem
                    key="maximum-article-width"
                    id="maximum-article-width"
                    icon={faExpandArrows}
                    text={t(`${tActionsNamespace}maximum-article-width`)}
                    onClick={onExpandArticle}
                    toggleEnabled
                    toggleActive={isArticleExpanded}
                    unavailableMessage={!isEdit ? t(`${tActionsNamespace}maximum-article-width-disabled`) : undefined}
                />
            )
        }

        if (onOpenInfo && !isArticleHistoryMode) {
            settings.push(
                <SettingsContextMenuItem
                    key="article-info"
                    id="article-info"
                    icon={faFile}
                    text={t(`${tActionsNamespace}article-info`)}
                    onClick={() => onOpenInfo(article, isEdit)}
                    useSeparator
                />
            )
        }

        if (
            currentCatalog &&
            currentCatalog.items[currentCatalog.rootId].data.newsline &&
            !isArticleHistoryMode &&
            !isScenario &&
            article.Status !== ArticleStatusType.Removed
        ) {
            settings.push(
                <SettingsContextMenuItem
                    key="newsline"
                    id="newsline"
                    icon={faNewspaper}
                    text={t(`${tActionsNamespace}newsline`)}
                    onClick={() => onOpenNewsline(article.NewslineSettigsDTO)}
                />
            )
        }

        if (onOpenSurvey && !isArticleHistoryMode) {
            settings.push(
                <SettingsContextMenuItem
                    key="survey"
                    id="survey"
                    icon={faFileEdit}
                    text={t(`${tActionsNamespace}survey`)}
                    onClick={() => onOpenSurvey(article, isEdit)}
                />
            )
        }
        if (!isArticleHistoryMode && !isScenario && article.Status !== ArticleStatusType.Removed) {
            settings.push(
                <SettingsContextMenuItem
                    key="article-actions-download-article"
                    id="article-actions-download-article"
                    icon={faArrowToBottom}
                    text={t(`${tActionsNamespace}download-article`)}
                    onClick={handleDownloadCatalog}
                    useSeparator
                />
            )
        }

        if (!isArticleHistoryMode && article.Status !== ArticleStatusType.Removed) {
            settings.push(
                <TooltipTrigger
                    id="article-import-files-info"
                    placement="left"
                    content={t(`${tActionsNamespace}import-files-tooltip`)}
                    condition={true}
                    key="article-actions-import-files-container"
                >
                    <div>
                        <SettingsContextMenuItem
                            id="article-actions-import-files"
                            key="article-actions-import-files"
                            icon={faFileImport}
                            text={t(`${tActionsNamespace}import-files`)}
                            onClick={importFilesOpenModal}
                            data-testid={testId.importFilesKB}
                        />
                    </div>
                </TooltipTrigger>
            )
        }

        if (!isScenario && !isEdit && !isArticleHistoryMode) {
            settings.push(
                <SettingsContextMenuItem
                    text={t(`${tNamespace}article-actions.compare-articles`)}
                    id={"article-actions-comparison-articles"}
                    key={"article-actions-comparison-articles"}
                    onClick={() => {
                        openModal()
                    }}
                    icon={faExchangeAlt}
                    useSeparator
                />
            )
        }

        if (onOpenHistory && !isScenario && !isEdit) {
            settings.push(
                <SettingsContextMenuItem
                    key={"article-actions-history"}
                    text={t(`${tNamespace}article-actions.article-history`)}
                    id={"article-actions-history"}
                    onClick={() => {
                        onOpenHistory(article)
                    }}
                    icon={faHistory}
                />
            )
        }

        if (onRefresh && !isArticleHistoryMode) {
            settings.push(
                <SettingsContextMenuItem
                    key="article-actions-update-check"
                    id="article-actions-update-check"
                    icon={faSync}
                    text={t(`${tActionsNamespace}update-check`)}
                    onClick={onRefresh}
                />
            )
        }

        if (!disableEditButtons && !isArticleHistoryMode) {
            if (article.Status === ArticleStatusType.Active) {
                settings.push(
                    <SettingsContextMenuItem
                        key="article-actions-set-unpublished"
                        id="article-actions-set-unpublished"
                        icon={faMinusCircle}
                        text={t(`${tActionsNamespace}set-unpublished-status`)}
                        onClick={handleSetUnpublishedStatus}
                        useSeparator
                        unavailableMessage={
                            isArticleLocked
                                ? t(`${tNamespace}article-changes-inheritance.functionality-locked`)
                                : undefined
                        }
                    />
                )
            }
            if (article.Status !== ArticleStatusType.Removed) {
                settings.push(
                    <SettingsContextMenuItem
                        key="article-actions-remove"
                        id="article-actions-remove"
                        icon={faTrashAlt}
                        text={t(`${tActionsNamespace}remove`)}
                        danger
                        onClick={
                            relatedArticlesState.data?.ChildCategories.length
                                ? removeArticleOpenModal
                                : handleRemoveArticle
                        }
                        unavailableMessage={
                            isArticleLocked
                                ? t(`${tNamespace}article-changes-inheritance.functionality-locked`)
                                : undefined
                        }
                    />
                )
            }
        }

        return settings
    }, [
        onExpandArticle,
        onOpenInfo,
        onOpenSurvey,
        onOpenNewsline,
        t,
        handleDownloadCatalog,
        onRefresh,
        disableEditButtons,
        isArticleExpanded,
        isEdit,
        article,
        handleSetUnpublishedStatus,
        relatedArticlesState.data?.ChildCategories.length,
        removeArticleOpenModal,
        handleRemoveArticle,
        currentCatalog
    ])

    return (
        <div className={cn(styles.articleActions)}>
            <KnowledgeBasePermissionsProvider
                permissions={article.Permissions}
                withNested={article.PermissionsWithNested}
            >
                {isFavoriteArticleButtonVisible && (
                    <FavoriteArticleButton article={article} disabled={!connected} categoryId={CurrentCategoryId} />
                )}
                {!disableEditButtons && (
                    <>
                        {!isArticleHistoryMode && (
                            <ArticleSharePopover
                                button={
                                    <IconButton
                                        type="submit"
                                        variant="link"
                                        icon={faShareAlt}
                                        className={cn(
                                            styles.articleActions__action,
                                            styles.articleActions__action_additional,
                                            articleSharePopoverShown && styles.articleActions__action_additional_active
                                        )}
                                        disabled={!connected}
                                        iconClassName="article-actions__icon"
                                        onClick={
                                            articleSharePopoverShown ? hideArticleSharePopover : showArticleSharePopover
                                        }
                                    >
                                        {t(`${tActionsNamespace}share`)}
                                    </IconButton>
                                }
                                show={articleSharePopoverShown}
                                onShare={onShare}
                                onCancelSharing={handleCancelArticlePopover}
                                onSaveSharing={hideArticleSharePopover}
                                permittedActionOptions={permittedActionOptions}
                                userAccessTitle={t(`${tNamespace}permissions.title`)}
                                projects={projects}
                                currentProject={currentProject}
                                sharingLink={sharingArticleState.data}
                                updateArticleLoading={updateArticleState.inProcess}
                                sharingLoading={sharingArticleState.inProcess}
                                removeSharingLoading={removeArticleSharingState.inProcess}
                                onCreateSharing={handleCreateArticleSharing}
                                onDeleteSharing={handleDeleteArticleSharing}
                                isSharingEnabled={isSharingEnabled}
                                isScenario={isScenario}
                                isArticleLocked={isArticleLocked}
                            />
                        )}

                        <Can permission={ModifyKnowledgeBaseSubscriptions} additionalCondition={!isArticleHistoryMode}>
                            <Async<KnowledgeBaseArticleSubscriptions>
                                dataState={subscriptionsState}
                                processView={<ArticleActionItemLoader />}
                                errorView={() => (
                                    <IconButton
                                        variant="link"
                                        icon={faExclamationCircle}
                                        className={cn(
                                            styles.articleActions__action,
                                            styles.articleActions__action_error
                                        )}
                                        disabled={!connected}
                                    >
                                        {t(`${tActionsNamespace}subscription`)}
                                    </IconButton>
                                )}
                            >
                                {({ data: subscriptions }) => (
                                    <ArticleSubscriptionPopover
                                        loading={updateSubscriptionsState.inProcess}
                                        show={articleSubscribePopoverShown}
                                        onSubmit={handleSubmitSubscriptions}
                                        onCancel={hideArticleSubscribePopover}
                                        articleSubscriptions={subscriptions}
                                        subscriptionOptions={subscriptionOptions}
                                        title={t(`${tNamespace}subscription.title`)}
                                        currentProject={currentProject}
                                        projects={projects}
                                    >
                                        <IconButton
                                            type="submit"
                                            variant="link"
                                            icon={faBell}
                                            className={cn(
                                                styles.articleActions__action,
                                                styles.articleActions__action_additional,
                                                articleSubscribePopoverShown &&
                                                    styles.articleActions__action_additional_active
                                            )}
                                            iconClassName="article-actions__icon"
                                            disabled={!connected}
                                            onClick={
                                                articleSubscribePopoverShown
                                                    ? hideArticleSubscribePopover
                                                    : showArticleSubscribePopover
                                            }
                                        >
                                            {t(`${tActionsNamespace}subscription`)}
                                        </IconButton>
                                    </ArticleSubscriptionPopover>
                                )}
                            </Async>
                        </Can>

                        <SettingsContextMenu items={getAdditionalSettings()}>
                            <Button
                                type="submit"
                                className={cn(styles.articleActions__action, styles.articleActions__action_additional)}
                                variant="link"
                                disabled={!connected}
                            >
                                <FontAwesomeIcon
                                    icon={faEllipsisHAlt}
                                    size="lg"
                                    className={cn(styles.articleActions__icon, styles.articleActions__icon_single)}
                                />
                            </Button>
                            {downloadLink}
                        </SettingsContextMenu>

                        {!isArticleHistoryMode && !isCompareMode && !isDelayedPublish && (
                            <TooltipTrigger
                                id="article-edit-button-blocked"
                                condition={!!isArticleLocked}
                                content={t(`${tNamespace}article-changes-inheritance.functionality-locked`)}
                                placement="bottom"
                            >
                                <Button
                                    type="submit"
                                    className={cn(
                                        styles.articleActions__action,
                                        styles.articleActions__action_edit,
                                        styles.articleActions__action_main
                                    )}
                                    variant="outline-primary"
                                    disabled={!connected || isArticleLocked}
                                    onClick={editButtonCallback}
                                >
                                    {editButtonTitle}
                                </Button>
                            </TooltipTrigger>
                        )}

                        {isEdit && (
                            <LoadingButton
                                type="submit"
                                loading={updateArticleState.inProcess}
                                className={cn(
                                    styles.articleActions__action,
                                    styles.articleActions__action_save,
                                    styles.articleActions__action_main
                                )}
                                variant="primary"
                                disabled={!connected}
                                onClick={onSave}
                            >
                                {t(`${tFormNamespace}save`)}
                            </LoadingButton>
                        )}

                        {!isEdit && publishButtonShown && !isArticleHistoryMode && !isCompareMode && !isDelayedPublish && (
                            <ArticlePublishPopover
                                show={articlePublishPopoverShown}
                                onCancel={hideArticlePublishPopover}
                                onSubmit={handlePublish}
                            >
                                <LoadingButton
                                    type="submit"
                                    loading={updateArticleState.inProcess}
                                    className={cn(
                                        styles.articleActions__action,
                                        styles.articleActions__action_publish,
                                        styles.articleActions__action_main
                                    )}
                                    variant="primary"
                                    disabled={!connected}
                                    onClick={handleClickPublishButton}
                                >
                                    <div className={styles.articleActions__action__withIcon}>
                                        <span>{t(`${tActionsNamespace}${publishAllowed ? "publish" : "check"}`)}</span>
                                        {publishAllowed && (
                                            <FontAwesomeIcon
                                                className={
                                                    articlePublishPopoverShown
                                                        ? styles.articleActions__action__withIcon_reverted
                                                        : styles.articleActions__action__withIcon_normal
                                                }
                                                icon={faAngleUp}
                                                size="lg"
                                            />
                                        )}
                                    </div>
                                </LoadingButton>
                            </ArticlePublishPopover>
                        )}

                        {!isEdit && !isArticleHistoryMode && !isCompareMode && isDelayedPublish ? (
                            <LoadingButton
                                type="submit"
                                loading={updateArticleState.inProcess}
                                className={cn(
                                    styles.articleActions__action,
                                    styles.articleActions__action_unpublish,
                                    styles.articleActions__action_main
                                )}
                                variant="primary"
                                disabled={!connected}
                                onClick={onUnpublishArticle}
                            >
                                {t(`${tActionsNamespace}cancel-delayed-publish`)}
                            </LoadingButton>
                        ) : null}

                        {showCreateDraftFromButton && (
                            <LoadingButton
                                type="submit"
                                loading={updateArticleState.inProcess}
                                className={cn(
                                    styles.articleActions__action,
                                    styles.articleActions__action_history,
                                    styles.articleActions__action_main
                                )}
                                variant="primary"
                                disabled={!connected}
                                onClick={() => {
                                    if (projectId) {
                                        dispatch(actions.setArticleHistoryMod(false))
                                        if (previousVersionArticleId) {
                                            dispatch(offCompareMode())
                                            dispatch(
                                                createDraftFromArticle(
                                                    projectId,
                                                    article.SymbolCode,
                                                    previousVersionArticleId
                                                )
                                            )
                                            return
                                        }
                                        dispatch(createDraftFromArticle(projectId, article.SymbolCode, article.Id))
                                    }
                                }}
                            >
                                {t(`${tNamespace}article-actions.create-draft-from`)}
                            </LoadingButton>
                        )}

                        {isScenario && isEdit && !isArticleHistoryMode && !isCompareMode && (
                            <AddButton
                                variant="outline-primary"
                                onClick={handleAddBlockClick}
                                className={cn(styles.articleActions__action, styles.articleActions__action_main)}
                                text={t(`${tScenarioEditorNamespace}add-block`)}
                                disabled={disableAddBlockBtn || !connected}
                            />
                        )}
                    </>
                )}
            </KnowledgeBasePermissionsProvider>
        </div>
    )
}

export default ArticleActions
